/**
 * Global Proximity Image Observer & Eager Preloader for KvikSign
 * 
 * Automatically observes images on all pages across the platform and eagerly preloads
 * them when the user approaches within 1200px of the viewport.
 * 
 * Benefits:
 * - Eliminates blank image placeholders and visible loading delay when scrolling down.
 * - Leaves initial page load (FCP/LCP) unblocked while making smooth, zero-delay scroll possible.
 * - Works universally across dynamically rendered components, article lists, and industry cards.
 */

let proximityObserver: IntersectionObserver | null = null;
const preloadedUrls = new Set<string>();

/**
 * Preloads an image URL into browser cache and decodes it.
 */
export function preloadImage(src: string): Promise<void> {
  if (!src || preloadedUrls.has(src)) {
    return Promise.resolve();
  }
  preloadedUrls.add(src);

  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = src;
    if (typeof img.decode === 'function') {
      img.decode().then(() => resolve()).catch(() => resolve());
    }
  });
}

/**
 * Initializes the global Proximity Image Observer.
 * Observes all <img> elements and triggers eager loading and decoding 1200px before
 * the user scrolls over them.
 */
export function initGlobalProximityImageObserver(): void {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    return;
  }

  // Idempotent initialization
  if (proximityObserver) {
    return;
  }

  // rootMargin of 1200px triggers loading well before the image enters viewport
  proximityObserver = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const target = entry.target as HTMLImageElement;
          
          // If the element has a data-src or data-srcset, promote it to src
          const dataSrc = target.getAttribute('data-src');
          if (dataSrc && target.src !== dataSrc) {
            target.src = dataSrc;
            target.removeAttribute('data-src');
          }

          // If standard img has a src, eagerly preload and decode
          if (target.src) {
            target.loading = 'eager';
            if (target.decode) {
              target.decode().catch(() => {});
            }
          }

          // Unobserve once triggered to save CPU
          observer.unobserve(target);
        }
      });
    },
    {
      root: null, // viewport
      rootMargin: '1200px 0px 1200px 0px', // 1200px buffer above and below
      threshold: 0.01,
    }
  );

  // Helper to observe all matching images in a root or node
  const observeImages = (root: ParentNode = document) => {
    if (!proximityObserver) return;
    const imgs = root.querySelectorAll('img');
    imgs.forEach((img) => {
      // Don't re-observe already loaded or eagerly loaded images if already visible
      if (!img.dataset.observedByProximity) {
        img.dataset.observedByProximity = 'true';
        proximityObserver.observe(img);
      }
    });
  };

  // Initial observe
  observeImages(document);

  // MutationObserver to automatically pick up dynamically rendered React pages, cards, and articles
  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement;
            if (el.tagName === 'IMG') {
              if (!el.dataset.observedByProximity && proximityObserver) {
                el.dataset.observedByProximity = 'true';
                proximityObserver.observe(el);
              }
            } else if (el.querySelectorAll) {
              observeImages(el);
            }
          }
        });
      }
    }
  });

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  // Background idle preload for primary homepage feature images
  const idlePreloadKeys = () => {
    const keyImages = [
      '/images/mitid-phone.webp',
      '/images/envelope.webp',
      '/images/pen.webp',
      '/images/digital-form.webp',
      '/images/the-office.webp',
      '/images/smartphone-notification.webp'
    ];

    keyImages.forEach((url) => {
      preloadImage(url);
    });
  };

  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(idlePreloadKeys, { timeout: 2000 });
  } else {
    setTimeout(idlePreloadKeys, 500);
  }
}
